import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react';
import { cn } from '../lib/classnames.js';

/**
 * GildedImage — an <img> that develops in like a print instead of popping.
 *
 * Pairs with `.img-develop` / `.is-loaded` in globals.css: the bitmap starts
 * a touch scaled and blurred, then settles over ~700ms once it decodes.
 * Cached images (img.complete on mount) skip straight to loaded so revisits
 * never re-perform.
 *
 * Backgrounded-tab safety: mobile browsers throttle hidden tabs and skip
 * image decode/paint, so a result that arrives while the user is in another
 * app can show as a blank (dark) plate. We force a decode and re-check
 * `complete` whenever the tab returns to the foreground, so the picture is
 * never left unpainted.
 *
 * Drop-in replacement for <img>; all native props pass through.
 */
export function GildedImage({
  className,
  onLoad,
  src,
  ...rest
}: ImgHTMLAttributes<HTMLImageElement>) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  // Cached bitmaps fire no load event — check synchronously after mount, and
  // re-arm whenever src changes (contact sheets reuse the same element).
  useEffect(() => {
    if (ref.current?.complete && ref.current.naturalWidth > 0) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
  }, [src]);

  // When the tab returns to the foreground, nudge the bitmap: a hidden tab may
  // have finished the network load without ever decoding/painting it.
  useEffect(() => {
    const settle = (): void => {
      const img = ref.current;
      if (!img) return;
      if (img.complete && img.naturalWidth > 0) {
        // decode() resolves once the bitmap is paint-ready; ignore failures
        // (older browsers / detached nodes) and just mark it loaded.
        img.decode?.().then(
          () => setLoaded(true),
          () => setLoaded(true),
        );
      }
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') settle();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', settle);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', settle);
    };
  }, []);

  return (
    <img
      ref={ref}
      src={src}
      {...rest}
      onLoad={(e) => {
        setLoaded(true);
        onLoad?.(e);
      }}
      className={cn('img-develop', loaded && 'is-loaded', className)}
    />
  );
}
